import type { MouseEvent } from 'react';
import { Fragment, useRef } from 'react';
import { useTranslate } from 'react-polyglot';
import { observer } from 'mobx-react';

import { Tooltip, useBreakpoint } from '@genially/design-system';
import { calculate } from '@genially/utils/build/module';

import { config } from '../../../../../../../../bootstrap/application/config/config';
import { useHostClientContext } from '../../../HostClientProvider/hooks/useHostClientContext';
import { HostGameBreakdownSteps } from '../../HostGame.constants';
import { useHostGameContext } from '../../HostGame.context';
import { useOpenQrCodeModal } from '../../hooks/useOpenQrCodeModal';
import { useToggleSessionLocked } from '../../hooks/useToggleSessionLocked';
import { HandQueueGameControlButton } from './components/HandQueueGameControlButton/HandQueueGameControlButton';
import { ParticipantsGameControlButton } from './components/ParticipantsGameControlButton/ParticipantsGameControlButton';
import { SettingsGameControlButton } from './components/SettingsGameControlButton/SettingsGameControlButton';
import {
  BlockedText,
  CentralSide,
  CentralSideWrapper,
  IconWrapper,
  LeftSide,
  PlayIconAndLinkWrapper,
  PlayLinkText,
  RightSide,
  SessionCode,
  SessionCodeText,
  StyledGeniallyIcon,
  StyledLockIcon,
  StyledUnlockIcon,
  Wrapper,
} from './GameControlHeader.styled';

export const GameControlHeader = observer(() => {
  const t = useTranslate();

  const wrapperRef = useRef<HTMLDivElement>(null);

  const { instructions, inviteSidebar, isOpenMode, initialSessionConfigModal } =
    useHostGameContext();

  // Hidden through the whole initial onboarding: while the config modal is up and, in open
  // mode, while the invite sidebar that modal opens is still on screen.
  const shouldShowHeader =
    (instructions.isVisible || instructions.isMouseNearTopOfIframe) &&
    !initialSessionConfigModal.isOpen &&
    !inviteSidebar.isOpen;
  const { client } = useHostClientContext();
  const { isSessionLocked, toggleIsSessionLocked } = useToggleSessionLocked();

  const { isDownwards, isUpwards } = useBreakpoint();

  const { openQrCodeModal } = useOpenQrCodeModal();

  const playUrl = new URL(config.urls.play).hostname;

  const isMobile = isDownwards(HostGameBreakdownSteps.forth);

  const handleToggleIsSessionBlocked = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    toggleIsSessionLocked();
  };

  // In open mode there is no separate QR code modal entry point for this area — it toggles
  // the invite sidebar directly, since that sidebar is already this host's invite screen.
  const handleOpenModal = () => {
    if (client.game.isSessionBlocked) return;

    if (isOpenMode) {
      inviteSidebar.toggle();
      return;
    }

    openQrCodeModal();
  };

  const CentralSideComponent = calculate(() => {
    if (isMobile) {
      return undefined;
    }

    return (
      <CentralSideWrapper
        data-cy="gameControlHeaderAccessCode"
        $isClickable={!isSessionLocked}
        onClick={handleOpenModal}
      >
        {isUpwards(HostGameBreakdownSteps.third) ? (
          <PlayIconAndLinkWrapper>
            <StyledGeniallyIcon />
            <PlayLinkText>{playUrl}</PlayLinkText>
          </PlayIconAndLinkWrapper>
        ) : undefined}

        {isSessionLocked ? (
          <Fragment>
            <BlockedText>
              {t('__new.play.navbarController.accessCode.closedTitle')}
            </BlockedText>

            <Tooltip text={t('__new.play.navbarController.accessCodeTitle.openTooltip')}>
              <IconWrapper onClick={handleToggleIsSessionBlocked}>
                <StyledLockIcon />
              </IconWrapper>
            </Tooltip>
          </Fragment>
        ) : (
          <Fragment>
            <SessionCodeText>
              {t('__new.play.navbarController.accessCode.openTitle')}{' '}
              <SessionCode as="span">{client.sessionId}</SessionCode>
            </SessionCodeText>

            <Tooltip text={t('__new.play.navbarController.accessCodeTitle.closeTooltip')}>
              <IconWrapper onClick={handleToggleIsSessionBlocked}>
                <StyledUnlockIcon />
              </IconWrapper>
            </Tooltip>
          </Fragment>
        )}
      </CentralSideWrapper>
    );
  });

  return (
    <Wrapper ref={wrapperRef} $isVisible={shouldShowHeader}>
      <LeftSide>
        <SettingsGameControlButton />

        <HandQueueGameControlButton />
        <ParticipantsGameControlButton
          isVisible={shouldShowHeader}
          toastParentRef={wrapperRef.current ?? undefined}
        />
      </LeftSide>
      <CentralSide>{CentralSideComponent}</CentralSide>
      <RightSide></RightSide>
    </Wrapper>
  );
});
